import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Ticket, Sparkles, Megaphone, Gift, ArrowRight, CheckCircle2,
  ChevronRight, DollarSign, Users, Clock, ShieldCheck, Send,
  ChevronDown,
} from 'lucide-react';
import SEO from '../components/SEO';
import { breadcrumbLD, faqLD } from '../utils/structuredData';

const SITE_URL = (typeof window !== 'undefined' && window.location?.origin) || 'https://eventism.com';

const HOW_IT_WORKS = [
  {
    n: '1',
    title: 'Apply in 60 seconds',
    body: 'Register your organization with name, email, password, and primary city. We pre-approve most accounts instantly.',
  },
  {
    n: '2',
    title: 'Build your event',
    body: 'Use our event builder + AI hero-image generator to launch a beautiful ticket page. Set tiers, dynamic pricing, custom questions, and waitlists.',
  },
  {
    n: '3',
    title: 'Promote & get paid',
    body: 'Run email + SMS campaigns straight from your dashboard. We payout via Stripe within 24h of your event.',
  },
];

const VALUE_TILES = [
  {
    icon: DollarSign,
    headline: 'Lower fees than Eventbrite',
    body: 'Flat 2.9% + $0.99/ticket — no percentage-of-the-organizer surcharge. Most organizers save 30–40% on every sold ticket.',
    tone: 'emerald',
    classes: {
      border: 'border-emerald-500/30',
      bg: 'from-emerald-500/10',
      icon_bg: 'bg-emerald-500/15',
      icon: 'text-emerald-400',
    },
  },
  {
    icon: Megaphone,
    headline: 'Marketing built in',
    body: 'Native email campaigns ($0.001/send), SMS blasts ($0.015 each), and AI-powered listing copy + hero images — all priced into your wallet.',
    tone: 'amber',
    classes: {
      border: 'border-amber-500/30',
      bg: 'from-amber-500/10',
      icon_bg: 'bg-amber-500/15',
      icon: 'text-amber-400',
    },
  },
  {
    icon: Gift,
    headline: '$25 free wallet credit',
    body: "Every new org gets $25 in their marketing wallet — enough to email ~25,000 ticket buyers or fire 1,600 SMS blasts before you spend a dollar.",
    tone: 'indigo',
    classes: {
      border: 'border-indigo-500/30',
      bg: 'from-indigo-500/10',
      icon_bg: 'bg-indigo-500/15',
      icon: 'text-indigo-400',
    },
  },
];

const FAQS = [
  {
    q: 'How does Eventism compare to Eventbrite, Ticketmaster, or DICE?',
    a: 'Eventism is purpose-built for independent organizers — flat fees, no percentage surcharges, built-in marketing tools, and 24-hour Stripe payouts. You keep more of every ticket, and your buyer experience matches premium platforms like DICE without the $5k/month enterprise pricing.',
  },
  {
    q: 'What kinds of events can I sell tickets to?',
    a: 'Concerts, festivals, brunches, comedy nights, parties, conferences, food experiences, sports leagues, art shows — anything ticketed. We currently serve Denver, Las Vegas, Los Angeles, and Charlotte, with more cities added quarterly.',
  },
  {
    q: 'When do I get paid?',
    a: 'Stripe Express handles payouts. Funds settle 24 hours after your event ends. For high-volume organizers, we offer rolling daily payouts after your third sold-out event.',
  },
  {
    q: 'What does it cost to list an event?',
    a: 'Listing is free. We charge a flat 2.9% + $0.99 per sold ticket — paid by the buyer at checkout (industry-standard). Marketing tools (email/SMS/AI) bill against your prepaid wallet so there are zero surprise charges.',
  },
  {
    q: 'Do you handle refunds and cancellations?',
    a: 'Yes — refund policies are configurable per ticket type (e.g., full refund up to 7 days out, 50% until 24h, none after). Refunds process through Stripe automatically.',
  },
  {
    q: 'Can I bring my own audience and email list?',
    a: 'Absolutely. Upload your CSV, segment by past behavior, and run campaigns from inside your dashboard. We don\'t hold your list hostage — you can export it any time.',
  },
];

const PRICING_ROWS = [
  { label: 'Listing your event', us: 'Free', them: 'Free' },
  { label: 'Ticket fee', us: '2.9% + $0.99', them: '3.7% + $1.79' },
  { label: 'Payout speed', us: '24 hours', them: '5–7 business days' },
  { label: 'Email campaigns', us: '$0.001 per send', them: '$0.025 per send' },
  { label: 'SMS blasts', us: '$0.015 per text', them: 'Add-on, $0.05+' },
  { label: 'AI listing + hero image', us: 'Included', them: 'Not available' },
  { label: 'Onboarding credit', us: '$25 wallet credit', them: 'None' },
];

export default function SellTickets() {
  const canonicalUrl = `${SITE_URL}/sell-tickets`;
  const breadcrumb = breadcrumbLD([
    { name: 'Home', url: '/' },
    { name: 'Sell Tickets', url: '/sell-tickets' },
  ]);
  const faqGraph = faqLD(FAQS.map((f) => ({ question: f.q, answer: f.a })));

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="sell-tickets-page">
      <SEO
        title="Sell Tickets Online — Best Eventbrite Alternative | Eventism"
        description="Eventism is the modern Eventbrite alternative for independent event organizers. Lower fees, faster payouts, AI-powered marketing, $25 free wallet credit. Start selling tickets in 60 seconds."
        canonical={canonicalUrl}
        url={canonicalUrl}
        keywords={[
          'sell tickets online', 'eventbrite alternative', 'event ticketing platform',
          'cheaper than eventbrite', 'host events', 'online ticket sales',
          'event organizer platform', 'sell tickets denver', 'sell tickets las vegas',
        ]}
        structuredData={[breadcrumb, faqGraph].filter(Boolean)}
      />

      {/* HERO */}
      <section className="relative max-w-6xl mx-auto px-4 pt-6 pb-12 sm:pb-20" data-testid="sell-tickets-hero">
        {/* Decorative blobs */}
        <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 -left-24 h-72 w-72 rounded-full bg-indigo-500/15 blur-3xl" />
          <div className="absolute -top-16 right-0 h-80 w-80 rounded-full bg-fuchsia-500/15 blur-3xl" />
          <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-amber-500/10 blur-3xl" />
        </div>

        <div className="grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3 space-y-6">
            <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-amber-400 flex items-center gap-2">
              <Ticket className="h-3.5 w-3.5" /> For event organizers
            </p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Space_Grotesk'] leading-[1.05]"
              data-testid="sell-tickets-h1"
            >
              Sell tickets in{' '}
              <span className="bg-gradient-to-r from-amber-400 via-fuchsia-400 to-indigo-400 bg-clip-text text-transparent">
                60 seconds
              </span>
              .
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-xl">
              Eventism is the modern Eventbrite alternative for independent organizers — lower fees,
              faster payouts, AI-powered marketing, and <strong className="text-foreground">$25 free</strong> in
              your wallet the moment you sign up.
            </p>

            <div className="flex flex-wrap gap-3 pt-2">
              <Link to="/organizations/apply" data-testid="hero-apply-cta">
                <Button className="h-12 px-7 text-base font-bold bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 hover:opacity-90 text-white border-0 shadow-lg shadow-fuchsia-500/20">
                  <Sparkles className="h-4 w-4 mr-2" /> Start selling tickets
                </Button>
              </Link>
              <a href="#how-it-works">
                <Button variant="outline" className="h-12 px-6 text-base">
                  How it works <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </a>
            </div>

            <div className="flex items-center gap-5 pt-3 text-xs text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> No setup fee</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Stripe payouts in 24h</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Cancel anytime</span>
            </div>
          </div>

          {/* Hero illustration: stacked tickets card */}
          <div className="lg:col-span-2">
            <Card className="border-border bg-card/80 backdrop-blur shadow-2xl shadow-fuchsia-500/10 rotate-1 hover:rotate-0 transition-transform">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-rose-400" />
                  <div className="h-2 w-2 rounded-full bg-amber-400" />
                  <div className="h-2 w-2 rounded-full bg-emerald-400" />
                  <span className="ml-auto text-[10px] font-mono text-muted-foreground">your.event/tickets</span>
                </div>
                <div className="aspect-video rounded-lg bg-gradient-to-br from-indigo-500/30 via-fuchsia-500/30 to-amber-500/30 flex items-center justify-center">
                  <Ticket className="h-12 w-12 text-white/80" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Your event</p>
                  <p className="font-bold text-lg">Summer Rooftop Series</p>
                  <p className="text-xs text-muted-foreground">Sat · Jul 18 · 8:00 PM · Denver</p>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg border border-border p-2">
                    <p className="text-[9px] uppercase text-muted-foreground">Early</p>
                    <p className="font-bold text-sm">$25</p>
                  </div>
                  <div className="rounded-lg border-2 border-amber-400 bg-amber-400/10 p-2">
                    <p className="text-[9px] uppercase text-amber-400 font-bold">GA</p>
                    <p className="font-bold text-sm">$35</p>
                  </div>
                  <div className="rounded-lg border border-border p-2">
                    <p className="text-[9px] uppercase text-muted-foreground">VIP</p>
                    <p className="font-bold text-sm">$75</p>
                  </div>
                </div>
                <Button className="w-full h-9 text-xs bg-amber-500 hover:bg-amber-400 text-black font-bold pointer-events-none">
                  Get Tickets <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* VALUE TILES */}
      <section className="max-w-6xl mx-auto px-4 mt-4 grid md:grid-cols-3 gap-4" data-testid="sell-tickets-values">
        {VALUE_TILES.map((t) => (
          <Card
            key={t.headline}
            className={`${t.classes.border} bg-gradient-to-br ${t.classes.bg} to-transparent`}
            data-testid={`value-${t.tone}`}
          >
            <CardContent className="p-6 space-y-3">
              <div className={`h-10 w-10 rounded-lg ${t.classes.icon_bg} flex items-center justify-center`}>
                <t.icon className={`h-5 w-5 ${t.classes.icon}`} />
              </div>
              <h3 className="font-bold text-lg">{t.headline}</h3>
              <p className="text-sm text-muted-foreground">{t.body}</p>
            </CardContent>
          </Card>
        ))}
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="max-w-6xl mx-auto px-4 mt-20 space-y-8" data-testid="sell-tickets-how">
        <div className="space-y-2">
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-fuchsia-400">How it works</p>
          <h2 className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk']">
            From idea to first ticket sold in one afternoon.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {HOW_IT_WORKS.map((s, i) => (
            <Card key={s.n} className="border-border relative" data-testid={`how-step-${s.n}`}>
              <CardContent className="p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center text-white font-bold">
                    {s.n}
                  </div>
                  {i < HOW_IT_WORKS.length - 1 && (
                    <ChevronRight className="h-5 w-5 text-muted-foreground hidden md:block" />
                  )}
                </div>
                <h3 className="font-bold text-lg">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="flex justify-center pt-2">
          <Link to="/organizations/apply" data-testid="how-apply-cta">
            <Button className="h-11 px-6 bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 text-white font-bold border-0">
              <Sparkles className="h-4 w-4 mr-2" /> Create my organization
            </Button>
          </Link>
        </div>
      </section>

      {/* PRICING TABLE */}
      <section className="max-w-5xl mx-auto px-4 mt-20 space-y-6" data-testid="sell-tickets-pricing">
        <div className="space-y-2 text-center">
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-amber-400">Pricing</p>
          <h2 className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk']">
            Side-by-side with Eventbrite.
          </h2>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            Flat, transparent fees. No setup, no monthly subscription, no hidden percentage-of-organizer surcharge.
          </p>
        </div>

        <Card className="border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-card">
                <tr className="border-b border-border">
                  <th className="text-left p-4 font-medium text-muted-foreground">Feature</th>
                  <th className="text-left p-4">
                    <div className="font-bold text-amber-400">Eventism</div>
                    <div className="text-[10px] uppercase text-muted-foreground">Our pricing</div>
                  </th>
                  <th className="text-left p-4">
                    <div className="font-bold text-muted-foreground">Eventbrite</div>
                    <div className="text-[10px] uppercase text-muted-foreground">Public 2026 pricing</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {PRICING_ROWS.map((row, i) => (
                  <tr key={row.label} className={i % 2 === 0 ? 'bg-background' : 'bg-card/50'}>
                    <td className="p-4 font-medium">{row.label}</td>
                    <td className="p-4 text-emerald-400 font-bold">{row.us}</td>
                    <td className="p-4 text-muted-foreground">{row.them}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <p className="text-[11px] text-muted-foreground text-center max-w-xl mx-auto">
          Comparison based on publicly listed Eventbrite Pro pricing as of Q1 2026. Your actual savings vary by ticket volume.
        </p>
      </section>

      {/* TRUST STRIP */}
      <section className="max-w-6xl mx-auto px-4 mt-20 grid sm:grid-cols-4 gap-4" data-testid="sell-tickets-trust">
        {[
          { icon: ShieldCheck, label: 'PCI-DSS via Stripe', icon_bg: 'bg-emerald-500/15', icon_color: 'text-emerald-400' },
          { icon: Users, label: '4 cities + growing', icon_bg: 'bg-indigo-500/15', icon_color: 'text-indigo-400' },
          { icon: Clock, label: '24-hour payouts', icon_bg: 'bg-amber-500/15', icon_color: 'text-amber-400' },
          { icon: Send, label: '99.9% deliverability', icon_bg: 'bg-fuchsia-500/15', icon_color: 'text-fuchsia-400' },
        ].map((t) => (
          <div key={t.label} className="flex items-center gap-3 p-4 rounded-lg border border-border bg-card/50">
            <div className={`h-8 w-8 rounded-md ${t.icon_bg} flex items-center justify-center`}>
              <t.icon className={`h-4 w-4 ${t.icon_color}`} />
            </div>
            <span className="text-sm font-medium">{t.label}</span>
          </div>
        ))}
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 mt-20 space-y-4" data-testid="sell-tickets-faq">
        <div className="space-y-2">
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-indigo-400">FAQ</p>
          <h2 className="text-3xl font-bold font-['Space_Grotesk']">Common questions</h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((f) => (
            <details
              key={f.q}
              className="group rounded-lg border border-border bg-card/50 open:bg-card transition-colors"
              data-testid={`faq-${f.q.slice(0, 24).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <summary className="cursor-pointer list-none p-4 flex items-center justify-between gap-3 font-bold">
                <span>{f.q}</span>
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground group-open:rotate-180 transition-transform" />
              </summary>
              <div className="px-4 pb-4 text-sm text-muted-foreground">{f.a}</div>
            </details>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-4xl mx-auto px-4 mt-20" data-testid="sell-tickets-final-cta">
        <Card className="border-amber-500/30 bg-gradient-to-br from-amber-500/15 via-fuchsia-500/15 to-indigo-500/15 overflow-hidden relative">
          <CardContent className="p-8 sm:p-12 text-center space-y-4 relative z-10">
            <h2 className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk']">
              Ready to sell your first ticket?
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
              Sign up in under a minute. We'll drop $25 into your marketing wallet so you can email or text your first 25,000 fans before paying a cent.
            </p>
            <div className="flex flex-wrap justify-center gap-3 pt-2">
              <Link to="/organizations/apply" data-testid="final-apply-cta">
                <Button className="h-12 px-8 text-base font-bold bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 hover:opacity-90 text-white border-0 shadow-lg shadow-fuchsia-500/30">
                  <Sparkles className="h-4 w-4 mr-2" /> Get started — it's free
                </Button>
              </Link>
              <Link to="/organizations">
                <Button variant="outline" className="h-12 px-6 text-base">
                  Browse the marketplace
                </Button>
              </Link>
            </div>
          </CardContent>
          <div className="absolute -bottom-12 -right-12 h-48 w-48 rounded-full bg-fuchsia-500/20 blur-3xl pointer-events-none" />
          <div className="absolute -top-12 -left-12 h-48 w-48 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />
        </Card>
      </section>
    </div>
  );
}
